"use client";

/*
 * Adapted from React Bits' CircularCarousel, https://reactbits.dev/c/components/circular-carousel
 *
 * MIT + Commons Clause License Condition v1.0. Copyright (c) 2026 David Haz.
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this
 * software and associated documentation files (the "Software"), to deal in the Software
 * without restriction, including without limitation the rights to use, copy, modify, merge,
 * publish, and distribute the Software as part of an application, website, or product,
 * subject to the following conditions: the above copyright notice and this permission notice
 * shall be included in all copies or substantial portions of the Software. You may use this
 * Software, including for any commercial purpose, so long as you do not sell, sublicense, or
 * redistribute the components themselves, whether alone, in a bundle, or as a ported
 * version. THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND.
 */

import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  type Ref,
} from "react";

/**
 * Changes from upstream, all in service of a portfolio on a white page:
 *
 * 1. **One shape.** Only the cylinder preset is kept; the orbit, wheel and panorama code
 *    paths are gone, which is most of the weight.
 * 2. **Faces, not only photos.** A card is drawn from an image, or from any node when there
 *    is no image (a client shown in the studio's own branding). The node is repeated in each
 *    curved strip exactly as an image would be.
 * 3. **Click to bring forward, click again to open.** Upstream fires its click callback for
 *    any card; here a card at the side only turns to the front, and the front card opens.
 * 4. **Nothing until it is near.** Images are not fetched and the frame loop does not start
 *    until the carousel is within a screen of the viewport, so it costs the hero nothing.
 * 5. **Controlled from outside.** The caption, the arrows and the counter belong to the
 *    section, which steps the ring through a ref.
 * 6. **Fades to white.** The depth fade resolves to the page colour.
 */

export interface CarouselItem {
  key: string;
  /** Accessible name of the slide. */
  label: string;
  /** An optimised image URL for the face. */
  src?: string;
  /** CSS object-position for the image. */
  position?: string;
  /** Drawn instead of an image when there is none. Fills the card. */
  face?: ReactNode;
}

export interface CarouselHandle {
  step: (delta: number) => void;
  focus: (index: number) => void;
}

type Tile = { index: number; total: number; start: number; end: number; size: number; move: string };
type Sample = { time: number; angle: number };
type Press = { id: number; x: number; y: number; angle: number; moved: boolean; origin: number; samples: Sample[] };

const TILES = 8;
const OVERLAP = 2.5;
const DRAG_THRESHOLD = 5;
const SPRING = 118;
const SETTLE_SPEED = 9;
const RISE_MS = 1400;
const PERSPECTIVE = 2500;
const TILT = -5;
const TO_RAD = Math.PI / 180;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const wrap = (degrees: number) => ((((degrees + 180) % 360) + 360) % 360) - 180;
const easeOutQuint = (t: number) => 1 - Math.pow(1 - t, 5);

const rotateX = (p: [number, number, number], degrees: number): [number, number, number] => {
  const r = degrees * TO_RAD;
  return [p[0], p[1] * Math.cos(r) - p[2] * Math.sin(r), p[1] * Math.sin(r) + p[2] * Math.cos(r)];
};
const rotateY = (p: [number, number, number], degrees: number): [number, number, number] => {
  const r = degrees * TO_RAD;
  return [p[0] * Math.cos(r) + p[2] * Math.sin(r), p[1], -p[0] * Math.sin(r) + p[2] * Math.cos(r)];
};

export const ClientCarousel = ({
  items,
  cardWidth = 300,
  aspectRatio = 0.8,
  gap = 30,
  speed = 7,
  momentum = 0.6,
  depthFade = 0.7,
  innerShade = 0.55,
  cornerRadius = 20,
  reduced = false,
  reach = 180,
  label = "Clients",
  onChange,
  onOpen,
  ref,
  className = "",
}: {
  items: CarouselItem[];
  cardWidth?: number;
  aspectRatio?: number;
  gap?: number;
  /** Idle drift, in degrees a second. */
  speed?: number;
  momentum?: number;
  depthFade?: number;
  innerShade?: number;
  cornerRadius?: number;
  reduced?: boolean;
  /**
   * How much of the ring, in degrees either side of the front, has to fit the box. 180 shows
   * the whole ring; a phone passes less, so the front card is large and its neighbours are
   * cut by the edges.
   */
  reach?: number;
  label?: string;
  onChange?: (index: number) => void;
  onOpen?: (index: number) => void;
  ref?: Ref<CarouselHandle>;
  className?: string;
}) => {
  const count = items.length;
  const cardW = cardWidth;
  const cardH = cardW / aspectRatio;
  const step = 360 / count;

  const radius = useMemo(() => {
    const n = Math.max(count, 3);
    const pitch = cardW + gap;
    const chord = pitch / (2 * Math.sin(Math.PI / n));
    const arc = (n * pitch) / (2 * Math.PI);
    return Math.max(arc, chord, cardW * 0.6);
  }, [count, cardW, gap]);

  // Each card is cut into vertical strips set on the curve, so a flat face bends with the
  // cylinder instead of sitting on it as a flat chord.
  const tiles = useMemo<Tile[]>(() => {
    const length = cardW / TILES;
    return Array.from({ length: TILES }, (_, index) => {
      const start = index * length - (index > 0 ? OVERLAP / 2 : 0);
      const end = (index + 1) * length + (index < TILES - 1 ? OVERLAP / 2 : 0);
      const center = (start + end) / 2 - cardW / 2;
      const alpha = center / radius;
      const shift = radius * Math.sin(alpha);
      const depth = -radius * (1 - Math.cos(alpha));
      const turn = (alpha * 180) / Math.PI;
      return {
        index,
        total: TILES,
        start,
        end,
        size: end - start,
        move: `translate3d(${shift}px, 0px, ${depth}px) rotateY(${turn}deg)`,
      };
    });
  }, [cardW, radius]);

  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const cameraRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const wakeRef = useRef<() => void>(() => {});
  const activeRef = useRef(0);
  const [active, setActive] = useState(0);
  const [near, setNear] = useState(false);
  const [ready, setReady] = useState(false);
  const [dragging, setDragging] = useState(false);
  const readyRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const settingsRef = useRef({ count, step, radius, speed, momentum, depthFade, reduced, cardH, reach });
  const measureRef = useRef<() => void>(() => {});

  // The frame loop reads the latest props through refs, so it never has to restart. They are
  // synced after each render, ahead of the effects below that read them.
  useLayoutEffect(() => {
    readyRef.current = ready;
    onChangeRef.current = onChange;
    settingsRef.current = { count, step, radius, speed, momentum, depthFade, reduced, cardH, reach };
  });

  const stateRef = useRef({
    angle: 0,
    velocity: 0,
    target: null as number | null,
    dir: -1,
    press: null as Press | null,
    drag: false,
    hover: false,
    pointer: { inside: false, x: 0, y: 0 },
    yaw: 0,
    pitch: 0,
    intro: null as number | null,
    introDone: false,
    holdUntil: 0,
    suppressClick: false,
    wheelTimer: undefined as ReturnType<typeof setTimeout> | undefined,
    fit: 1,
    drop: 0,
    last: 0,
  });

  /* Near the viewport: only then are the faces fetched and the loop allowed to run. */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const io = new IntersectionObserver(([entry]) => entry.isIntersecting && setNear(true), { rootMargin: "100% 0px" });
    io.observe(root);
    return () => io.disconnect();
  }, []);

  const sourcesKey = items.map((item) => item.src ?? "").join("|");
  useEffect(() => {
    if (!near) return;
    let cancelled = false;
    const load = (src: string) =>
      new Promise<void>((resolve) => {
        if (!src) return resolve();
        const image = new Image();
        image.decoding = "async";
        image.onload = () => (image.decode ? image.decode().then(resolve, resolve) : resolve());
        image.onerror = () => resolve();
        image.src = src;
      });
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, 2400));
    Promise.race([Promise.all(sourcesKey.split("|").map(load)), timeout]).then(() => {
      if (cancelled) return;
      stateRef.current.introDone = false;
      stateRef.current.intro = null;
      setReady(true);
      wakeRef.current();
    });
    return () => {
      cancelled = true;
    };
  }, [near, sourcesKey]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const stage = stageRef.current;
    const camera = cameraRef.current;
    const ring = ringRef.current;
    if (!root || !stage || !camera || !ring) return;
    const state = stateRef.current;
    let raf = 0;
    let visible = false;

    const nearest = (angle: number) => Math.round(angle / settingsRef.current.step) * settingsRef.current.step;

    // Scales the stage so the whole ring, at its widest, fits the box it was given.
    const measure = () => {
      const s = settingsRef.current;
      const rect = root.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const width = rect.width * 0.96;
      const height = rect.height * 0.92;
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;
      const corners: [number, number][] = [
        [-cardW / 2, -cardH / 2],
        [cardW / 2, -cardH / 2],
        [-cardW / 2, cardH / 2],
        [cardW / 2, cardH / 2],
      ];
      for (let a = -s.reach; a <= s.reach; a += s.reach / 24) {
        for (const [cx, cy] of corners) {
          let p = rotateY([cx, cy, s.radius], a);
          p = [p[0], p[1], p[2] - s.radius];
          p = rotateX(p, TILT);
          if (p[2] >= PERSPECTIVE * 0.95) continue;
          const k = PERSPECTIVE / (PERSPECTIVE - p[2]);
          minX = Math.min(minX, p[0] * k);
          maxX = Math.max(maxX, p[0] * k);
          minY = Math.min(minY, p[1] * k);
          maxY = Math.max(maxY, p[1] * k);
        }
      }
      const fit = Math.min(1, width / Math.max(maxX - minX, 1), height / Math.max(maxY - minY, 1));
      state.fit = fit;
      state.drop = (rect.height / fit) * 0.55 + s.cardH;
      stage.style.perspective = `${PERSPECTIVE}px`;
      stage.style.transform = `translate3d(0, ${-((minY + maxY) / 2) * fit}px, 0) scale(${fit})`;
    };
    measureRef.current = measure;

    const advance = (dt: number, now: number) => {
      const s = settingsRef.current;
      if (!state.introDone && readyRef.current) {
        if (state.intro === null) {
          if (s.reduced) state.introDone = true;
          else state.intro = now;
        }
        if (state.intro !== null && now - state.intro >= RISE_MS) {
          state.intro = null;
          state.introDone = true;
        }
      }

      const paused = state.hover || state.drag || now < state.holdUntil;
      const cruise = !s.reduced && !paused && state.intro === null ? s.speed * state.dir : 0;
      let busy = state.intro !== null || state.drag;

      if (state.drag || state.intro !== null) {
        if (!state.drag) state.velocity = 0;
      } else if (state.target !== null) {
        let remaining = dt;
        const damping = 2 * Math.sqrt(SPRING);
        while (remaining > 0) {
          const h = Math.min(remaining, 1 / 240);
          state.velocity += (SPRING * (state.target - state.angle) - damping * state.velocity) * h;
          state.angle += state.velocity * h;
          remaining -= h;
        }
        if (Math.abs(state.target - state.angle) < 0.004 && Math.abs(state.velocity) < 0.03) {
          state.angle = state.target;
          state.velocity = 0;
          state.target = null;
        }
        busy = true;
      } else {
        const tau = 0.18 + s.momentum * 1.5;
        state.velocity += (cruise - state.velocity) * (1 - Math.exp(-dt / tau));
        state.angle += state.velocity * dt;
        if (cruise === 0 && Math.abs(state.velocity) < SETTLE_SPEED) state.target = nearest(state.angle);
        busy = busy || cruise !== 0 || Math.abs(state.velocity) > 0.01 || state.target !== null;
      }
      if (now < state.holdUntil) busy = true;

      // A little parallax toward the pointer, so the ring feels held rather than printed.
      const ease = 1 - Math.exp(-dt / 0.35);
      const aimYaw = state.pointer.inside && !s.reduced ? state.pointer.x * 2.7 : 0;
      const aimPitch = state.pointer.inside && !s.reduced ? -state.pointer.y * 1.8 : 0;
      state.yaw += (aimYaw - state.yaw) * ease;
      state.pitch += (aimPitch - state.pitch) * ease;
      if (Math.abs(aimYaw - state.yaw) > 0.01 || Math.abs(aimPitch - state.pitch) > 0.01) busy = true;
      return busy;
    };

    const render = (now: number) => {
      const s = settingsRef.current;
      const elapsed = state.intro !== null ? now - state.intro : 0;
      const R = s.radius * (1 + 0.06 * Math.min(1, Math.abs(state.velocity) / 420));
      camera.style.transform = `translate3d(0, 0, ${-R}px) rotateX(${TILT + state.pitch}deg) rotateY(${state.yaw}deg)`;
      ring.style.transform = `rotateY(${state.angle}deg)`;

      for (let index = 0; index < s.count; index++) {
        const card = cardRefs.current[index];
        if (!card) continue;
        const base = index * s.step;
        let transform = `rotateY(${base}deg) translateZ(${R}px)`;
        if (state.intro !== null) {
          // The cards rise into the ring one after another, the front one first.
          const away = Math.abs(wrap(base + state.angle));
          const p = easeOutQuint(clamp((elapsed - (away / 180) * 480) / 900, 0, 1));
          if (p < 1) transform += ` translateY(${(1 - p) * state.drop}px)`;
        }
        card.style.transform = transform;
        const facing = Math.cos(wrap(base + state.angle) * TO_RAD);
        card.style.setProperty("--cc-depth", (s.depthFade * Math.pow((1 - facing) / 2, 1.25)).toFixed(3));
      }

      const index = ((Math.round(-state.angle / s.step) % s.count) + s.count) % s.count || 0;
      if (index !== activeRef.current) {
        activeRef.current = index;
        setActive(index);
        onChangeRef.current?.(index);
      }
    };

    const frame = (now: number) => {
      raf = 0;
      const dt = state.last ? Math.min((now - state.last) / 1000, 0.05) : 1 / 60;
      state.last = now;
      const busy = advance(dt, now);
      render(now);
      if (busy && visible && !document.hidden) raf = requestAnimationFrame(frame);
      else state.last = 0;
    };

    const wake = () => {
      if (!raf && visible && !document.hidden && readyRef.current) raf = requestAnimationFrame(frame);
    };
    wakeRef.current = wake;

    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(raf);
        raf = 0;
        state.last = 0;
      } else wake();
    };

    const resize = new ResizeObserver(() => {
      measure();
      render(performance.now());
      wake();
    });
    resize.observe(root);

    // Off screen, the loop stops dead; the drift is decoration and costs nothing unseen.
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) wake();
      else {
        cancelAnimationFrame(raf);
        raf = 0;
        state.last = 0;
      }
    });
    io.observe(root);

    // A sideways trackpad swipe turns the ring; a vertical one is left to the page.
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      const perPixel = 180 / (Math.PI * settingsRef.current.radius * state.fit);
      state.target = null;
      state.angle -= event.deltaX * perPixel;
      state.velocity = -event.deltaX * perPixel * 30;
      state.holdUntil = performance.now() + 1600;
      clearTimeout(state.wheelTimer);
      state.wheelTimer = setTimeout(() => {
        state.target = nearest(state.angle + state.velocity * 0.12);
        wake();
      }, 140);
      wake();
    };
    root.addEventListener("wheel", onWheel, { passive: false });
    document.addEventListener("visibilitychange", onVisibility);

    measure();
    render(performance.now());
    wake();

    return () => {
      cancelAnimationFrame(raf);
      resize.disconnect();
      io.disconnect();
      clearTimeout(state.wheelTimer);
      root.removeEventListener("wheel", onWheel);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [cardW, cardH]);

  useLayoutEffect(() => {
    measureRef.current();
  }, [reach, radius]);

  // Settings changed (or the faces became ready): give the loop a nudge.
  useEffect(() => {
    wakeRef.current();
  });

  const focus = useCallback((index: number) => {
    const state = stateRef.current;
    const s = settingsRef.current;
    let target = -index * s.step;
    target += 360 * Math.round((state.angle - target) / 360);
    state.target = target;
    state.holdUntil = performance.now() + 2800;
    wakeRef.current();
  }, []);

  const stepBy = useCallback((delta: number) => {
    const state = stateRef.current;
    const s = settingsRef.current;
    const base = state.target ?? Math.round(state.angle / s.step) * s.step;
    state.target = base - delta * s.step;
    state.holdUntil = performance.now() + 2800;
    wakeRef.current();
  }, []);

  useImperativeHandle(ref, () => ({ step: stepBy, focus }), [stepBy, focus]);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    const state = stateRef.current;
    state.suppressClick = false;
    if (event.button !== 0) return;
    state.press = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      angle: state.angle,
      moved: false,
      origin: 0,
      samples: [{ time: performance.now(), angle: state.angle }],
    };
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const state = stateRef.current;
    if (event.pointerType === "mouse") {
      const rect = rootRef.current?.getBoundingClientRect();
      if (rect) {
        state.pointer.inside = true;
        state.pointer.x = clamp(((event.clientX - rect.left) / rect.width) * 2 - 1, -1, 1);
        state.pointer.y = clamp(((event.clientY - rect.top) / rect.height) * 2 - 1, -1, 1);
      }
    }
    const press = state.press;
    if (!press || press.id !== event.pointerId) {
      wakeRef.current();
      return;
    }
    const delta = event.clientX - press.x;
    const cross = event.clientY - press.y;
    if (!press.moved) {
      if (Math.abs(delta) < DRAG_THRESHOLD) return;
      // A mostly vertical swipe on touch is the page scrolling, not the ring.
      if (Math.abs(cross) > Math.abs(delta) * 1.2 && event.pointerType !== "mouse") {
        state.press = null;
        return;
      }
      press.moved = true;
      press.origin = delta;
      state.drag = true;
      state.target = null;
      state.velocity = 0;
      setDragging(true);
      try {
        rootRef.current?.setPointerCapture(event.pointerId);
      } catch {
        /* The pointer is already gone. */
      }
    }
    const perPixel = 180 / (Math.PI * settingsRef.current.radius * state.fit);
    state.angle = press.angle + (delta - press.origin) * perPixel;
    const now = performance.now();
    press.samples.push({ time: now, angle: state.angle });
    while (press.samples.length > 2 && now - press.samples[0].time > 110) press.samples.shift();
    wakeRef.current();
  };

  const release = (event: PointerEvent<HTMLDivElement>) => {
    const state = stateRef.current;
    const press = state.press;
    if (!press || press.id !== event.pointerId) return;
    state.press = null;
    if (!press.moved) return;
    state.drag = false;
    setDragging(false);
    state.suppressClick = true;
    const first = press.samples[0];
    const last = press.samples[press.samples.length - 1];
    const span = (last.time - first.time) / 1000;
    const velocity = span > 0.008 ? clamp((last.angle - first.angle) / span, -1400, 1400) : 0;
    state.velocity = velocity;
    if (Math.abs(velocity) > 60) state.dir = Math.sign(velocity);
    const tau = 0.18 + settingsRef.current.momentum * 1.5;
    state.target = Math.round((state.angle + velocity * tau * 0.55) / settingsRef.current.step) * settingsRef.current.step;
    wakeRef.current();
  };

  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const state = stateRef.current;
    if (state.suppressClick) {
      state.suppressClick = false;
      return;
    }
    const card = (event.target as HTMLElement).closest?.("[data-cc-index]");
    if (!card) return;
    const index = Number(card.getAttribute("data-cc-index"));
    if (index === activeRef.current) onOpen?.(index);
    else focus(index);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowRight") stepBy(1);
    else if (event.key === "ArrowLeft") stepBy(-1);
    else if (event.key === "Home") focus(0);
    else if (event.key === "End") focus(count - 1);
    else if (event.key === "Enter" || event.key === " ") onOpen?.(activeRef.current);
    else return;
    event.preventDefault();
  };

  const renderTile = (item: CarouselItem, tile: Tile, back: boolean) => {
    const strip = back ? tile.total - 1 - tile.index : tile.index;
    const r = "var(--cc-radius)";
    const first = strip === 0;
    const last = strip === tile.total - 1;
    const offset = back ? cardW - tile.end : tile.start;
    const face: CSSProperties = { left: -offset, top: 0, width: cardW, height: cardH };
    return (
      <div
        key={`${back ? "b" : "f"}${tile.index}`}
        className="absolute [backface-visibility:hidden] [outline:1px_solid_transparent]"
        style={{
          left: -tile.size / 2,
          top: -cardH / 2,
          width: tile.size,
          height: cardH,
          transform: tile.move + (back ? " rotateY(180deg)" : ""),
        }}
        aria-hidden="true"
      >
        <div
          className="absolute left-0 top-0 w-full overflow-hidden"
          style={{ height: cardH, borderRadius: `${first ? r : 0} ${last ? r : 0} ${last ? r : 0} ${first ? r : 0}` }}
        >
          {item.src ? (
            // A plain img on purpose: each face is cut into strips on a 3D ring, and the
            // next/image wrapper would add a box per strip for nothing. The src is already
            // an optimised URL from the image endpoint.
            // eslint-disable-next-line @next/next/no-img-element
            <img
              className="pointer-events-none absolute block max-w-none select-none object-cover [-webkit-user-drag:none]"
              src={item.src}
              alt=""
              draggable={false}
              decoding="async"
              style={{ ...face, objectPosition: item.position }}
            />
          ) : (
            <div className="pointer-events-none absolute" style={face}>
              {item.face}
            </div>
          )}
          {back && <div className="pointer-events-none absolute inset-0 bg-brand-ink opacity-[var(--cc-inner)]" />}
          <div className="pointer-events-none absolute inset-0 bg-white opacity-[var(--cc-depth,0)]" />
        </div>
      </div>
    );
  };

  return (
    <div
      ref={rootRef}
      className={`group/cc relative h-full w-full touch-pan-y select-none overflow-hidden outline-none [-webkit-tap-highlight-color:transparent] focus-visible:ring-2 focus-visible:ring-brand-accent data-[dragging]:cursor-grabbing ${dragging ? "" : "cursor-grab"} ${className}`.trim()}
      style={
        {
          "--cc-radius": `${cornerRadius}px`,
          "--cc-inner": (1 - innerShade).toFixed(3),
        } as CSSProperties
      }
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
      tabIndex={0}
      data-ready={ready ? "" : undefined}
      data-dragging={dragging ? "" : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={release}
      onPointerCancel={release}
      onPointerEnter={(event) => {
        if (event.pointerType !== "mouse") return;
        stateRef.current.hover = true;
        wakeRef.current();
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse") {
          stateRef.current.hover = false;
          stateRef.current.pointer.inside = false;
        }
        wakeRef.current();
      }}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      <div
        ref={stageRef}
        className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-500 group-data-[ready]/cc:opacity-100 motion-reduce:transition-none"
      >
        <div ref={cameraRef} className="relative h-0 w-0 [transform-style:preserve-3d]">
          <div ref={ringRef} className="absolute left-0 top-0 h-0 w-0 [transform-style:preserve-3d]">
            {near &&
              items.map((item, index) => (
                <div
                  key={item.key}
                  ref={(element) => {
                    cardRefs.current[index] = element;
                  }}
                  className="absolute left-0 top-0 h-0 w-0 cursor-pointer [transform-style:preserve-3d]"
                  data-cc-index={index}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${item.label}, ${index + 1} of ${count}`}
                >
                  {tiles.map((tile) => renderTile(item, tile, false))}
                  {tiles.map((tile) => renderTile(item, tile, true))}
                </div>
              ))}
          </div>
        </div>
      </div>
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {`${items[active]?.label ?? ""}, ${active + 1} of ${count}`}
      </div>
    </div>
  );
};
